// Training types
export const TRAINING_TYPES = ['open', 'assigned'] as const
export type TrainingType = (typeof TRAINING_TYPES)[number]

// Training enrollment statuses (text column, not pgEnum for flexibility)
export const TRAINING_ENROLLMENT_STATUSES = [
  'applied',
  'pending_approval',
  'confirmed',
  'in_progress',
  'completed',
  'in_follow_up',
  'closed',
  'rejected',
  'cancelled',
] as const
export type TrainingEnrollmentStatus = (typeof TRAINING_ENROLLMENT_STATUSES)[number]

/** 定員を消費しない（席を空ける）受講ステータス */
export const SEAT_RELEASED_ENROLLMENT_STATUSES: readonly TrainingEnrollmentStatus[] = [
  'rejected',
  'cancelled',
]

/** 研修の受講を終えた受講ステータス（完了後にフォローアップ中・終了へ進む） */
export const FINISHED_ENROLLMENT_STATUSES: readonly TrainingEnrollmentStatus[] = [
  'completed',
  'in_follow_up',
  'closed',
]

export function consumesSeat(status: string): boolean {
  return !SEAT_RELEASED_ENROLLMENT_STATUSES.includes(status as TrainingEnrollmentStatus)
}

/** 残席数。定員未設定は null（無制限）、定員超過でも 0 未満にしない。seatCount は consumesSeat な受講数 */
export function calcRemainingSeats(capacity: number | null, seatCount: number): number | null {
  return capacity === null ? null : Math.max(capacity - seatCount, 0)
}

export function hasFinishedTraining(status: string): boolean {
  return FINISHED_ENROLLMENT_STATUSES.includes(status as TrainingEnrollmentStatus)
}

// Approval statuses
export const APPROVAL_STATUSES = ['pending', 'approved', 'rejected', 'proxy_approved'] as const
export type ApprovalStatus = (typeof APPROVAL_STATUSES)[number]

// Follow-up statuses（本人・上司の評価提出状況から決まる）
export const FOLLOW_UP_STATUSES = ['pending', 'self_submitted', 'completed'] as const
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number]

// Notification types
export const NOTIFICATION_TYPES = [
  'enrollment_confirmation',
  'approval_request',
  'enrollment_approved',
  'enrollment_rejected',
  'assignment_notice',
  'assignment_supervisor_notice',
  'followup_opened',
  'followup_submitted',
  'followup_commented',
  'followup_evaluation_returned',
  'reminder_followup',
  'reminder_followup_supervisor',
  'reminder_approval',
  'reminder_survey',
  'escalation',
  'training_updated',
] as const
export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

/** 通知タイプの表示名（通知テンプレート管理画面で使用） */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  enrollment_confirmation: '受講申込の受付',
  approval_request: '上司への承認依頼',
  enrollment_approved: '受講申込の承認',
  enrollment_rejected: '受講申込の却下',
  assignment_notice: '受講者への割当のお知らせ',
  assignment_supervisor_notice: '上司への割当のお知らせ',
  followup_opened: 'スキルチェックの入力開始',
  followup_submitted: 'スキルチェックの提出',
  followup_commented: 'スキルチェックへのコメント',
  followup_evaluation_returned: 'スキルチェック評価の差し戻し',
  reminder_followup: 'スキルチェック未提出のリマインド（本人）',
  reminder_followup_supervisor: 'スキルチェック未提出のリマインド（上司）',
  reminder_approval: '承認期限超過のリマインド',
  reminder_survey: 'アンケート未回答のリマインド',
  escalation: '承認期限超過のエスカレーション',
  training_updated: '研修内容の変更のお知らせ',
}

/**
 * 通知タイプごとに本文・件名で使える変数（Mustache 形式 {{変数名}}）。
 * 送信側が渡す変数と一致させること
 */
const FOLLOW_UP_VARIABLES = [
  'trainingTitle',
  'followUpLabel',
  'followUpDueDate',
  'traineeName',
] as const

export const NOTIFICATION_TYPE_VARIABLES: Record<NotificationType, readonly string[]> = {
  enrollment_confirmation: ['userName', 'trainingTitle'],
  approval_request: ['applicantName', 'trainingTitle'],
  enrollment_approved: ['userName', 'trainingTitle'],
  enrollment_rejected: ['userName', 'trainingTitle'],
  assignment_notice: ['userName', 'trainingTitle'],
  assignment_supervisor_notice: ['traineeName', 'trainingTitle'],
  followup_opened: FOLLOW_UP_VARIABLES,
  followup_submitted: FOLLOW_UP_VARIABLES,
  followup_commented: FOLLOW_UP_VARIABLES,
  followup_evaluation_returned: FOLLOW_UP_VARIABLES,
  reminder_followup: FOLLOW_UP_VARIABLES,
  reminder_followup_supervisor: FOLLOW_UP_VARIABLES,
  reminder_approval: ['applicantName', 'trainingTitle'],
  reminder_survey: [],
  escalation: ['overdueCount'],
  training_updated: ['trainingTitle'],
}

// Notification channels
export const NOTIFICATION_CHANNELS = ['teams', 'email'] as const
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number]

/**
 * 通知チャネルの表示名と提供状況（唯一の定義元）。
 * available=false は enum・DB 値としては存在するが、設定として選べない（メールは Phase 2）
 */
export const NOTIFICATION_CHANNEL_OPTIONS: readonly {
  value: NotificationChannel
  label: string
  available: boolean
}[] = [
  { value: 'teams', label: 'Teams', available: true },
  { value: 'email', label: 'メール', available: false },
]

export const AVAILABLE_NOTIFICATION_CHANNELS: readonly NotificationChannel[] =
  NOTIFICATION_CHANNEL_OPTIONS.filter((o) => o.available).map((o) => o.value)

export function isNotificationChannelAvailable(channel: string): boolean {
  return AVAILABLE_NOTIFICATION_CHANNELS.includes(channel as NotificationChannel)
}

/** 保存済みの値が未提供のチャネルなら、利用可能な先頭のチャネルに置き換える */
export function resolveAvailableNotificationChannel(channel: string): NotificationChannel {
  return isNotificationChannelAvailable(channel)
    ? (channel as NotificationChannel)
    : AVAILABLE_NOTIFICATION_CHANNELS[0]
}

// Notification delivery statuses（notification_logs は送信キュー兼ログ）
// queued: 配信待ち / accepted: 送信先（Power Automate 等）が受け付けた / failed: 配信を断念
export const NOTIFICATION_DELIVERY_STATUSES = ['queued', 'accepted', 'failed'] as const
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number]
